import type { AdsAnalytics, AdsCampaign, AdsDayPoint, AdsPeriodDays, AdsTotals } from "./ads-analytics";

/**
 * DEMONSTRATION data source. Numbers are generated deterministically from the client id so
 * the screen is stable between reloads, and every response is flagged `source: "DEMO"` so
 * the UI always says so. Replace this source (not the screen) when real data exists.
 */
const hash = (text: string): number => {
  let value = 2166136261;
  for (const char of text) { value ^= char.charCodeAt(0); value = Math.imul(value, 16777619); }
  return value >>> 0;
};
const random = (seed: number): (() => number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const round = (value: number, places = 2): number => { const f = 10 ** places; return Math.round(value * f) / f; };

type CampaignModel = Readonly<{ id: string; name: string; platform: AdsCampaign["platform"]; status: AdsCampaign["status"]; share: number; ctr: number; cpc: number; cvr: number; ticket: number }>;
const MODELS: readonly CampaignModel[] = [
  { id: "demo-1", name: "Pesquisa · Marca", platform: "GOOGLE_ADS", status: "ACTIVE", share: 0.16, ctr: 0.082, cpc: 1.1, cvr: 0.11, ticket: 125 },
  { id: "demo-2", name: "Pesquisa · Genéricas", platform: "GOOGLE_ADS", status: "ACTIVE", share: 0.27, ctr: 0.041, cpc: 2.35, cvr: 0.052, ticket: 140 },
  { id: "demo-3", name: "Remarketing · Carrinho", platform: "META_ADS", status: "ACTIVE", share: 0.2, ctr: 0.026, cpc: 1.45, cvr: 0.085, ticket: 115 },
  { id: "demo-4", name: "Prospecção · Interesses", platform: "META_ADS", status: "ACTIVE", share: 0.25, ctr: 0.014, cpc: 1.95, cvr: 0.024, ticket: 98 },
  { id: "demo-5", name: "Display · Awareness", platform: "GOOGLE_ADS", status: "PAUSED", share: 0.12, ctr: 0.006, cpc: 0.72, cvr: 0.008, ticket: 85 },
];

type Day = { date: string; spend: number; impressions: number; clicks: number; conversions: number; revenue: number; perCampaign: Map<string, { spend: number; impressions: number; clicks: number; conversions: number }> };

const buildDays = (clientId: string, endDate: Date, total: number): Day[] => {
  const rand = random(hash(`ads-demo:${clientId}`));
  const days: Day[] = [];
  const baseDaily = 380 + (hash(clientId) % 240);
  for (let index = total - 1; index >= 0; index -= 1) {
    const date = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate() - index));
    const weekday = date.getUTCDay();
    const weekFactor = weekday === 0 || weekday === 6 ? 0.72 : 1;
    const trend = 0.85 + ((total - index) / total) * 0.3;
    const day: Day = { date: date.toISOString().slice(0, 10), spend: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0, perCampaign: new Map() };
    for (const model of MODELS) {
      const active = model.status === "ACTIVE" || index > 20;
      const spend = active ? baseDaily * model.share * weekFactor * trend * (0.82 + rand() * 0.36) : 0;
      const clicks = spend / model.cpc * (0.94 + rand() * 0.12);
      const impressions = clicks / model.ctr * (0.95 + rand() * 0.1);
      const conversions = Math.round(clicks * model.cvr * (0.8 + rand() * 0.4));
      const row = { spend: round(spend), impressions: Math.round(impressions), clicks: Math.round(clicks), conversions };
      day.perCampaign.set(model.id, row);
      day.spend += row.spend; day.impressions += row.impressions; day.clicks += row.clicks; day.conversions += row.conversions;
      day.revenue += conversions * model.ticket;
    }
    days.push(day);
  }
  return days;
};

const totalsOf = (days: readonly Day[]): AdsTotals => {
  const sum = (pick: (d: Day) => number): number => days.reduce((acc, d) => acc + pick(d), 0);
  const spend = round(sum((d) => d.spend)), impressions = sum((d) => d.impressions), clicks = sum((d) => d.clicks);
  const conversions = sum((d) => d.conversions), revenue = round(sum((d) => d.revenue));
  return {
    spend, impressions, clicks, conversions, revenue,
    ctr: impressions ? round((clicks / impressions) * 100) : 0,
    cpc: clicks ? round(spend / clicks) : 0,
    cpa: conversions ? round(spend / conversions) : 0,
    roas: spend ? round(revenue / spend) : 0,
  };
};

const change = (current: number, previous: number): number => (previous ? round(((current - previous) / previous) * 100, 1) : 0);

export const createDemoAdsAnalytics = (input: Readonly<{ clientId: string; days: AdsPeriodDays; now?: Date }>): AdsAnalytics => {
  const now = input.now ?? new Date();
  const all = buildDays(input.clientId, now, input.days * 2);
  const current = all.slice(input.days);
  const previous = all.slice(0, input.days);
  const totals = totalsOf(current), before = totalsOf(previous);
  const series: AdsDayPoint[] = current.map((d) => ({ date: d.date, spend: round(d.spend), impressions: d.impressions, clicks: d.clicks, conversions: d.conversions }));
  const campaigns: AdsCampaign[] = MODELS.map((model) => {
    const rows = current.map((d) => d.perCampaign.get(model.id)!);
    const spend = round(rows.reduce((a, r) => a + r.spend, 0)), impressions = rows.reduce((a, r) => a + r.impressions, 0);
    const clicks = rows.reduce((a, r) => a + r.clicks, 0), conversions = rows.reduce((a, r) => a + r.conversions, 0);
    return { id: model.id, name: model.name, platform: model.platform, status: model.status, spend, impressions, clicks, conversions,
      ctr: impressions ? round((clicks / impressions) * 100) : 0, cpc: clicks ? round(spend / clicks) : 0 };
  }).sort((a, b) => b.spend - a.spend);
  return {
    source: "DEMO", days: input.days, currency: "BRL", generatedAt: now.toISOString(), totals, series, campaigns,
    changePercent: {
      spend: change(totals.spend, before.spend), impressions: change(totals.impressions, before.impressions), clicks: change(totals.clicks, before.clicks),
      conversions: change(totals.conversions, before.conversions), revenue: change(totals.revenue, before.revenue), ctr: change(totals.ctr, before.ctr),
      cpc: change(totals.cpc, before.cpc), cpa: change(totals.cpa, before.cpa), roas: change(totals.roas, before.roas),
    },
  };
};
